import { useEffect, useState } from "react";

/**
 * A game's logs site (Warcraft Logs flavour, FF Logs, ...), from GET /games.
 * Server twin: auth/lib/game_logs.js. null = the game has no known logs site,
 * so nothing is auto-built for it (a pasted link still works).
 */
export type GameLogs = { provider: string; name: string; origin: string };

let gamesPromise: Promise<Map<string, GameLogs | null>> | null = null;

function loadGameLogs(): Promise<Map<string, GameLogs | null>> {
  if (!gamesPromise) {
    gamesPromise = fetch("/games", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: Array<{ id: string; logs?: GameLogs | null }>) =>
        new Map(rows.map((g) => [g.id, g.logs || null] as [string, GameLogs | null]))
      )
      .catch(() => {
        gamesPromise = null; // let the next caller retry
        return new Map<string, GameLogs | null>();
      });
  }
  return gamesPromise;
}

/** The logs site for one game; undefined while loading, null when it has none. */
export function useGameLogs(gameId: string | null | undefined): GameLogs | null | undefined {
  const [logs, setLogs] = useState<GameLogs | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    if (!gameId) { setLogs(null); return; }
    setLogs(undefined);
    loadGameLogs().then((m) => { if (!cancelled) setLogs(m.get(gameId) ?? null); });
    return () => { cancelled = true; };
  }, [gameId]);
  return logs;
}

/**
 * A character's page on the game's logs site, or "" when the game has no logs
 * site or a part is missing. Our realm slugs carry the realm type
 * ("spineshatter-pvp"); logs sites do not.
 */
export function characterLogsUrl(
  logs: GameLogs | null | undefined,
  { region, realmSlug, name }: { region: string; realmSlug: string; name: string }
): string {
  if (!logs || !region || !realmSlug || !name.trim()) return "";
  const realm = realmSlug.toLowerCase().replace(/-(pvp|pve|rp|rppvp|normal)$/, "");
  return `${logs.origin}/character/${region.toLowerCase()}/${realm}/${name.toLowerCase().trim()}`;
}
